/**
 * AppointmentResponsePage
 * Public page for responding to appointment via token (email/whatsapp links)
 *
 * This page does not require authentication and is used for:
 * - Confirming appointments
 * - Cancelling appointments
 * - Requesting reschedule
 *
 * @version 2025.1.0
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Schedule,
  MedicalServices,
  Done,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import appointmentService from '../../services/appointmentService';

type ActionType = 'confirm' | 'cancel' | 'reschedule';

export const AppointmentResponsePage: React.FC = () => {
  const { id, token } = useParams<{ id: string; token: string }>();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Get action from URL query params
  const action = searchParams.get('action') as ActionType | null;

  useEffect(() => {
    // Auto-execute action if specified in URL
    if (action && id && token) {
      executeAction(action);
    } else {
      setLoading(false);
    }
  }, [action, id, token]);

  const executeAction = async (actionType: ActionType) => {
    if (!id || !token) {
      setError('Link inválido');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const appointmentId = parseInt(id, 10);

      switch (actionType) {
        case 'confirm':
          await appointmentService.confirmByToken(appointmentId, token);
          setSuccess('Agendamento confirmado com sucesso!');
          break;
        case 'cancel':
          await appointmentService.cancelByToken(appointmentId, token);
          setSuccess('Agendamento cancelado com sucesso.');
          break;
        case 'reschedule':
          await appointmentService.requestRescheduleByToken(appointmentId, token);
          setSuccess('Solicitação de reagendamento registrada. Entraremos em contato em breve.');
          break;
        default:
          setError('Ação inválida');
      }
    } catch (err: unknown) {
      console.error('Error executing action:', err);
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Erro ao processar sua solicitação';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!id || !token) return;
    setActionLoading(true);
    try {
      await appointmentService.confirmByToken(parseInt(id, 10), token);
      setSuccess('Agendamento confirmado com sucesso!');
      setError(null);
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Erro ao confirmar agendamento';
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!id || !token) return;
    setActionLoading(true);
    try {
      await appointmentService.cancelByToken(parseInt(id, 10), token);
      setSuccess('Agendamento cancelado com sucesso.');
      setError(null);
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Erro ao cancelar agendamento';
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReschedule = async () => {
    if (!id || !token) return;
    setActionLoading(true);
    try {
      await appointmentService.requestRescheduleByToken(parseInt(id, 10), token);
      setSuccess('Solicitação de reagendamento registrada. Entraremos em contato em breve.');
      setError(null);
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Erro ao solicitar reagendamento';
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          Processando...
        </Typography>
      </Box>
    );
  }

  // Show result after auto-action
  if (success || (action && !error)) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
        p={3}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Done sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            {action === 'confirm' && 'Confirmado!'}
            {action === 'cancel' && 'Cancelado'}
            {action === 'reschedule' && 'Solicitação Enviada'}
            {!action && 'Sucesso!'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {success}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Show error
  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
        p={3}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom color="error">
            Ops!
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            {error}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Se o problema persistir, entre em contato com o suporte.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Show action buttons (no auto-action)
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="background.default"
      p={3}
    >
      <Paper sx={{ p: 4, maxWidth: 600, width: '100%' }}>
        <Box textAlign="center" mb={4}>
          <MedicalServices sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Confirmação de Agendamento
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Selecione uma opção abaixo para responder ao seu agendamento
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Stack spacing={2}>
          <Button
            variant="contained"
            color="success"
            size="large"
            fullWidth
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
            onClick={handleConfirm}
            disabled={actionLoading || !!success}
          >
            Confirmar Presença
          </Button>

          <Button
            variant="outlined"
            color="warning"
            size="large"
            fullWidth
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <Schedule />}
            onClick={handleReschedule}
            disabled={actionLoading || !!success}
          >
            Solicitar Reagendamento
          </Button>

          <Button
            variant="outlined"
            color="error"
            size="large"
            fullWidth
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <Cancel />}
            onClick={handleCancel}
            disabled={actionLoading || !!success}
          >
            Cancelar Agendamento
          </Button>
        </Stack>

        {success && (
          <Alert severity="success" sx={{ mt: 3 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        <Typography variant="caption" color="text.disabled" display="block" textAlign="center" mt={4}>
          Este link é válido apenas para este agendamento.
        </Typography>
      </Paper>
    </Box>
  );
};

export default AppointmentResponsePage;
